if (!customElements.get('koota-format-scroller')) {
  class KootaFormatScroller extends HTMLElement {
    constructor() {
      super();
      this.current = 0;
      this.interacted = false;
      this.hintTimers = [];
      this.abortController = new AbortController();
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = window.matchMedia('(max-width: 749px)');
    }

    connectedCallback() {
      if (this.abortController.signal.aborted) this.abortController = new AbortController();

      this.track = this.querySelector('[data-koota-format-track]');
      this.cards = Array.from(this.querySelectorAll('[data-koota-format-card]'));
      this.dots = Array.from(this.querySelectorAll('[data-koota-format-dot]'));
      this.nextButton = this.querySelector('[data-koota-format-next]');
      this.nextLabel = this.querySelector('[data-koota-format-next-label]');
      if (!this.track || this.cards.length < 2) return;

      const signal = this.abortController.signal;
      this.track.addEventListener('touchstart', () => this.markInteracted(), { passive: true, signal });
      this.track.addEventListener('pointerdown', () => this.markInteracted(), { passive: true, signal });
      this.track.addEventListener('keydown', () => this.markInteracted(), { signal });

      this.nextButton?.addEventListener('click', () => {
        this.markInteracted();
        this.scrollToCard(this.current === this.cards.length - 1 ? 0 : this.current + 1);
      }, { signal });

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this.markInteracted();
          this.scrollToCard(index);
        }, { signal });
      });

      this.observeCards();
      this.observeVisibility();
      this.setCurrent(0);
    }

    disconnectedCallback() {
      this.abortController.abort();
      this.cardObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.hintTimers.forEach((timer) => window.clearTimeout(timer));
    }

    observeCards() {
      if (!('IntersectionObserver' in window)) return;
      this.cardObserver = new IntersectionObserver((entries) => {
        const visibleCard = entries
          .filter((entry) => entry.isIntersecting)
          .sort((first, second) => second.intersectionRatio - first.intersectionRatio)[0];
        if (!visibleCard || visibleCard.intersectionRatio < 0.55) return;
        this.setCurrent(this.cards.indexOf(visibleCard.target));
      }, {
        root: this.track,
        threshold: [0.55, 0.7, 0.85]
      });
      this.cards.forEach((card) => this.cardObserver.observe(card));
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window)) {
        this.classList.add('is-in-view');
        return;
      }

      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.classList.toggle('is-in-view', entry.isIntersecting);
        if (!entry.isIntersecting || entry.intersectionRatio < 0.35) return;
        this.playHint();
      }, { threshold: [0, 0.35] });
      this.visibilityObserver.observe(this);
    }

    setCurrent(index) {
      if (index < 0) return;
      this.current = index;
      this.cards.forEach((card, cardIndex) => card.classList.toggle('is-current', cardIndex === index));
      this.dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', String(active));
      });

      if (this.nextLabel) {
        this.nextLabel.textContent = index === this.cards.length - 1
          ? 'Revoir le café en grains'
          : 'Voir le café moulu';
      }
    }

    markInteracted() {
      this.interacted = true;
      this.classList.remove('is-hinting');
      this.hintTimers.forEach((timer) => window.clearTimeout(timer));
      this.hintTimers = [];
    }

    playHint() {
      if (!this.mobile.matches || this.reducedMotion.matches || this.interacted || this.hintTimers.length) return;

      this.classList.add('is-hinting');
      this.hintTimers.push(window.setTimeout(() => {
        if (!this.interacted) this.track.scrollBy({ left: 42, behavior: 'smooth' });
      }, 650));
      this.hintTimers.push(window.setTimeout(() => {
        if (!this.interacted) this.track.scrollTo({ left: 0, behavior: 'smooth' });
      }, 1800));
      this.hintTimers.push(window.setTimeout(() => {
        this.classList.remove('is-hinting');
      }, 2900));
    }

    scrollToCard(index) {
      const card = this.cards[index];
      if (!card) return;
      this.setCurrent(index);
      card.scrollIntoView({
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    }
  }

  customElements.define('koota-format-scroller', KootaFormatScroller);
}

if (!customElements.get('koota-home-advisor')) {
  class KootaHomeAdvisor extends HTMLElement {
    constructor() {
      super();
      this.answers = {};
      this.currentStep = 1;
      this.abortController = new AbortController();
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    }

    connectedCallback() {
      if (this.abortController.signal.aborted) this.abortController = new AbortController();

      this.products = this.parseProducts();
      this.intro = this.querySelector('[data-koota-advisor-intro]');
      this.flow = this.querySelector('[data-koota-advisor-flow]');
      this.result = this.querySelector('[data-koota-advisor-result]');
      this.progress = this.querySelector('[data-koota-advisor-progress]');
      this.progressBar = this.querySelector('[data-koota-advisor-progress-bar]');
      this.progressLabel = this.querySelector('[data-koota-advisor-progress-label]');
      this.cartForm = this.querySelector('[data-koota-advisor-cart-form]');
      this.cartStatus = this.querySelector('[data-koota-advisor-cart-status]');
      this.productLink = this.querySelector('[data-koota-advisor-product-link]');
      this.productLinkLabel = this.productLink?.textContent || '';

      const signal = this.abortController.signal;
      this.querySelector('[data-koota-advisor-start]')?.addEventListener('click', () => this.start(), { signal });
      this.querySelector('[data-koota-advisor-restart]')?.addEventListener('click', () => this.restart(), { signal });
      this.cartForm?.addEventListener('submit', (event) => this.prepareNativeCartForm(event), { signal });
      this.querySelectorAll('[data-koota-advisor-answer]').forEach((button) => {
        button.setAttribute('aria-pressed', 'false');
        button.addEventListener('click', () => this.selectAnswer(button), { signal });
      });
    }

    disconnectedCallback() {
      this.abortController.abort();
    }

    parseProducts() {
      try {
        const parsed = JSON.parse(this.querySelector('[data-koota-advisor-products]')?.textContent || '[]')
          .filter((product) => product && product.id && product.variantId);
        const uniqueProducts = new Map();
        parsed.forEach((product) => {
          const isCoffee = product.kind === 'coffee' || product.kind === 'fallback-coffee';
          const uniqueKey = isCoffee
            ? `${product.id}:${product.variantId}`
            : `${product.id}:${product.kind}`;
          const existing = uniqueProducts.get(uniqueKey);
          if (!existing || (existing.kind === 'fallback-coffee' && product.kind === 'coffee')) {
            uniqueProducts.set(uniqueKey, product);
          }
        });
        return Array.from(uniqueProducts.values());
      } catch (_error) {
        return [];
      }
    }

    normalize(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
    }

    formatMoney(cents) {
      return new Intl.NumberFormat(this.dataset.locale || 'fr-FR', {
        style: 'currency',
        currency: this.dataset.currency || 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(cents || 0) / 100);
    }

    start() {
      if (!this.products.length) {
        const startButton = this.querySelector('[data-koota-advisor-start]');
        if (startButton) {
          startButton.disabled = true;
          startButton.textContent = 'Configurez la collection de cafés';
        }
        return;
      }

      this.intro.hidden = true;
      this.result.hidden = true;
      this.flow.hidden = false;
      this.showStep(1);
      this.pushEvent('koota_home_quiz_start');
    }

    selectAnswer(button) {
      const key = button.dataset.kootaAdvisorAnswer;
      const value = button.dataset.value;
      if (!key || !value) return;

      button.parentElement?.querySelectorAll('[data-koota-advisor-answer]').forEach((candidate) => {
        const selected = candidate === button;
        candidate.classList.toggle('is-selected', selected);
        candidate.setAttribute('aria-pressed', String(selected));
      });

      this.answers[key] = value;
      if (key === 'machine') this.answers.format = button.dataset.format || 'moulu';

      this.pushEvent('koota_home_quiz_answer', {
        question: key,
        answer: value,
      });

      const delay = this.reducedMotion.matches ? 0 : 180;
      window.setTimeout(() => {
        if (key === 'pack') {
          this.renderResult();
        } else {
          this.showStep(this.currentStep + 1);
        }
      }, delay);
    }

    showStep(step) {
      this.currentStep = Math.min(Math.max(step, 1), 3);
      this.querySelectorAll('[data-koota-advisor-step]').forEach((panel) => {
        const active = Number(panel.dataset.kootaAdvisorStep) === this.currentStep;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
        if (active) {
          window.requestAnimationFrame(() => panel.querySelector('h3')?.focus({ preventScroll: true }));
        }
      });

      this.progress?.setAttribute('aria-valuenow', String(this.currentStep));
      if (this.progressLabel) this.progressLabel.textContent = `Question ${this.currentStep} sur 3`;
      if (this.progressBar) this.progressBar.style.transform = `scaleX(${this.currentStep / 3})`;
    }

    productScore(product) {
      const identity = this.normalize([
        product.title,
        product.format,
        product.profile,
        product.method,
        product.advice,
      ].join(' '));
      const intensity = Number.parseFloat(String(product.intensity || '').replace(',', '.')) || 0;
      const productFormat = this.productFormat(product);
      const formatScore = Number(this.dataset.formatScore || 90);
      const profileScore = Number(this.dataset.profileScore || 60);
      const intensityScore = Number(this.dataset.intensityScore || 2);
      let score = 0;

      if (this.answers.format === 'grains') {
        score += productFormat === 'grains' ? formatScore : -(formatScore + 30);
      } else if (this.answers.format === 'moulu') {
        score += productFormat === 'moulu' ? formatScore : -(formatScore + 30);
      }

      if (this.answers.taste === 'harmonieux') {
        if (/harmonieux|doux|equilibr|rond/.test(identity)) score += profileScore;
        score += Math.max(0, 9 - Math.abs(7 - intensity)) * intensityScore;
      } else if (this.answers.taste === 'puissant') {
        if (/puissant|intense|corse|boise|epice/.test(identity)) score += profileScore;
        score += intensity * intensityScore;
      } else if (this.answers.taste === 'colombie' || this.answers.taste === 'colombia') {
        if (/colombie|colombia|delicat|acidul|vif|vive|origine/.test(identity)) score += profileScore;
        score += Math.max(0, 9 - Math.abs(6 - intensity)) * intensityScore;
      } else if (this.answers.taste === 'discovery') {
        const expectedKind = this.answers.format === 'grains' ? 'discovery-grains' : 'discovery-ground';
        if (product.kind === expectedKind) score += 180;
        else if (/coffret|decouverte|assortiment|plusieurs profils/.test(identity)) score += 95;
        else score -= 20;
      }

      return score;
    }

    productFormat(product) {
      if (product.kind === 'discovery-grains') return 'grains';
      if (product.kind === 'discovery-ground') return 'moulu';
      const identity = this.normalize(`${product.title} ${product.format}`);
      return identity.includes('moulu') ? 'moulu' : 'grains';
    }

    availableUnits(product) {
      if (!product?.available) return 0;
      if (!product.inventory?.tracked || product.inventory?.policy === 'continue') {
        return Number.POSITIVE_INFINITY;
      }
      return Math.max(0, Number(product.inventory.quantity || 0));
    }

    rankedProducts() {
      return this.products
        .map((product, index) => ({ product, index, score: this.productScore(product) }))
        .filter(({ product }) => this.productFormat(product) === this.answers.format)
        .sort((first, second) => second.score - first.score || first.index - second.index)
        .map(({ product }) => product);
    }

    recommendationPlan() {
      const desiredSachets = Math.max(2, Number(this.answers.pack || 2));
      const ranked = this.rankedProducts();
      const primaryRanked = ranked.filter((product) => product.kind !== 'fallback-coffee');
      const fallbackRanked = ranked.filter((product) => product.kind === 'fallback-coffee');
      const firstChoice = primaryRanked[0] || fallbackRanked[0];
      const firstAvailable = primaryRanked.find((product) => this.availableUnits(product) > 0)
        || fallbackRanked.find((product) => this.availableUnits(product) > 0);
      if (!firstAvailable) return null;
      const firstUnits = Math.max(1, Number(firstAvailable.unitsPerProduct || 1));
      const desiredQuantity = Math.ceil(desiredSachets / firstUnits);

      if (this.availableUnits(firstAvailable) >= desiredQuantity) {
        return {
          product: firstAvailable,
          quantity: desiredQuantity,
          sachetQuantity: desiredQuantity * firstUnits,
          requestedQuantity: desiredSachets,
          stockMessage: firstChoice && firstChoice.id !== firstAvailable.id
            ? firstAvailable.kind === 'fallback-coffee'
              ? `${firstChoice.title} est indisponible. Nous vous redirigeons vers ${firstAvailable.title}, une alternative d’une autre gamme dans le même format.`
              : `${firstChoice.title} est indisponible. ${firstAvailable.title} est proposé automatiquement dans le même format.`
            : '',
        };
      }

      const fullQuantityAlternative = [...primaryRanked, ...fallbackRanked].find((product) => (
        product.id !== firstAvailable.id
        && this.availableUnits(product) >= Math.ceil(desiredSachets / Math.max(1, Number(product.unitsPerProduct || 1)))
      ));
      if (fullQuantityAlternative) {
        const alternativeUnits = Math.max(1, Number(fullQuantityAlternative.unitsPerProduct || 1));
        const alternativeQuantity = Math.ceil(desiredSachets / alternativeUnits);
        return {
          product: fullQuantityAlternative,
          quantity: alternativeQuantity,
          sachetQuantity: alternativeQuantity * alternativeUnits,
          requestedQuantity: desiredSachets,
          stockMessage: fullQuantityAlternative.kind === 'fallback-coffee'
            ? `Le premier choix manque de stock. Nous vous redirigeons vers ${fullQuantityAlternative.title}, une autre gamme dans le même format, pour conserver la quantité souhaitée.`
            : `Le premier choix manque de stock. Nous vous proposons ${fullQuantityAlternative.title}, dans le même format, pour conserver la quantité souhaitée.`,
        };
      }

      const reducedQuantity = Math.max(1, Math.min(
        desiredQuantity,
        Number(this.availableUnits(firstAvailable))
      ));
      return {
        product: firstAvailable,
        quantity: reducedQuantity,
        sachetQuantity: reducedQuantity * firstUnits,
        requestedQuantity: desiredSachets,
        stockMessage: `La quantité disponible correspond à environ ${reducedQuantity * firstUnits} sachet${reducedQuantity * firstUnits > 1 ? 's' : ''}. Elle a été ajustée automatiquement.`,
      };
    }

    defaultProfile() {
      return {
        harmonieux: 'Doux, fruité et équilibré',
        puissant: 'Corsé, boisé et généreux',
        colombie: 'Fin, fruité et légèrement acidulé',
        colombia: 'Fin, fruité et légèrement acidulé',
        discovery: 'Plusieurs profils à découvrir',
      }[this.answers.taste] || 'Profil KOOTA';
    }

    recommendationReason(product) {
      const machine = {
        broyeur: 'votre machine avec broyeur',
        moulin: 'votre moulin et votre méthode de préparation',
        'sans-broyeur': 'votre cafetière sans broyeur',
      }[this.answers.machine] || (this.answers.format === 'grains' ? 'votre machine ou votre moulin' : 'votre cafetière');
      const taste = {
        harmonieux: 'une tasse douce et équilibrée',
        puissant: 'un café corsé qui conserve sa présence, même avec du lait',
        colombie: 'une tasse plus fine, fruitée et légèrement vive',
        colombia: 'une tasse plus fine, fruitée et légèrement vive',
        discovery: 'alterner plusieurs profils selon le moment',
      }[this.answers.taste] || 'votre profil';

      return `Nous l’avons retenu pour ${taste}, dans un format adapté à ${machine}. ${
        product.advice || product.method || ''
      }`.trim();
    }

    packInsight(quantity, estimatedCups) {
      const uses = {
        2: 'Idéal en solo ou pour découvrir ce profil sans surstocker.',
        4: 'Idéal à deux, avec un rythme régulier d’une à deux tasses par personne.',
        6: 'Idéal pour un foyer où le café accompagne plusieurs moments de la journée.',
        10: 'Idéal pour une grande famille ou une petite équipe ; ouvrez les sachets au fur et à mesure.',
      };

      return `${uses[quantity] || 'Une quantité ajustée à votre rythme.'} Environ ${estimatedCups} tasses au total, calculées à 9 g par tasse.`;
    }

    renderResult() {
      const plan = this.recommendationPlan();
      if (!plan) {
        this.showNoStockResult();
        return;
      }

      const product = plan.product;
      const quantity = plan.quantity;
      const unitsPerProduct = Math.max(1, Number(product.unitsPerProduct || 1));
      const productWeight = Number(product.weight || 0);
      const weight = productWeight > 0 ? productWeight : 250 * unitsPerProduct;
      const cupLow = Number(product.price || 0) * 8 / weight;
      const cupHigh = Number(product.price || 0) * 10 / weight;
      const estimatedCups = Math.floor(weight * quantity / 9);
      const total = Number(product.price || 0) * quantity;
      const intensity = String(product.intensity || '').replace(/\/10.*$/i, '') || '—';

      let image = this.querySelector('[data-koota-advisor-image]');
      if (!image && product.image?.src) {
        const media = this.querySelector('.koota-home-advisor__result-media');
        if (media) {
          image = document.createElement('img');
          image.width = 800;
          image.height = 800;
          image.loading = 'lazy';
          image.setAttribute('data-koota-advisor-image', '');
          media.prepend(image);
        }
      }
      if (image && product.image?.src) {
        image.src = product.image.src;
        image.alt = product.image.alt || product.title;
        image.width = Number(product.image.width || 800);
        image.height = Number(product.image.height || 800);
      }

      this.setText('[data-koota-advisor-title]', product.title);
      this.setText('[data-koota-advisor-reason]', this.recommendationReason(product));
      this.setText('[data-koota-advisor-profile]', product.profile || this.defaultProfile());
      this.setText('[data-koota-advisor-intensity]', intensity === '—' ? intensity : `${intensity}/10`);
      this.setText(
        '[data-koota-advisor-method]',
        product.method || (this.answers.format === 'grains' ? 'Machine à grains ou moulin' : 'Cafetière pour café moulu')
      );
      this.setText(
        '[data-koota-advisor-cup-price]',
        `${this.formatMoney(cupLow)}–${this.formatMoney(cupHigh)}`
      );
      this.setText(
        '[data-koota-advisor-pack-label]',
        unitsPerProduct > 1
          ? `${quantity} coffret${quantity > 1 ? 's' : ''} · environ ${plan.sachetQuantity} sachets`
          : `Pack de ${quantity} conseillé`
      );
      this.setText('[data-koota-advisor-pack-price]', this.formatMoney(total));
      this.setText('[data-koota-advisor-pack-insight]', this.packInsight(plan.requestedQuantity, estimatedCups));
      const stockNote = this.querySelector('[data-koota-advisor-stock-note]');
      if (stockNote) {
        stockNote.hidden = !plan.stockMessage;
        stockNote.textContent = plan.stockMessage;
      }
      this.setText(
        '[data-koota-advisor-add-label]',
        unitsPerProduct > 1
          ? `Ajouter ${quantity} coffret${quantity > 1 ? 's' : ''} · ${this.formatMoney(total)}`
          : `Ajouter ${quantity} sachets · ${this.formatMoney(total)}`
      );

      const variantInput = this.querySelector('[data-koota-advisor-variant]');
      const quantityInput = this.querySelector('[data-koota-advisor-quantity]');
      if (variantInput) variantInput.value = product.variantId;
      if (quantityInput) quantityInput.value = quantity;
      this.activePlan = plan;

      const productLink = this.querySelector('[data-koota-advisor-product-link]');
      if (productLink) {
        productLink.href = product.url;
        productLink.textContent = this.productLinkLabel;
      }
      if (this.cartForm) this.cartForm.hidden = false;

      this.flow.hidden = true;
      this.result.hidden = false;
      this.result.classList.add('is-visible');
      window.requestAnimationFrame(() => this.result.querySelector('h3')?.focus({ preventScroll: true }));

      this.pushEvent('koota_home_quiz_result', {
        product_id: String(product.id),
        variant_id: String(product.variantId),
        pack_quantity: quantity,
        product_name: product.title,
        stock_adjusted: Boolean(plan.stockMessage),
      });
    }

    showNoStockResult() {
      this.activePlan = null;
      this.flow.hidden = true;
      this.result.hidden = false;
      this.result.classList.add('is-visible');
      this.setText('[data-koota-advisor-title]', 'Ce format revient bientôt.');
      this.setText(
        '[data-koota-advisor-reason]',
        'Aucun produit disponible ne correspond actuellement à votre machine. Nous préférons vous le dire plutôt que de proposer un format inadapté.'
      );
      this.setText('[data-koota-advisor-profile]', this.defaultProfile());
      this.setText('[data-koota-advisor-intensity]', '—');
      this.setText(
        '[data-koota-advisor-method]',
        this.answers.format === 'grains' ? 'Machine ou moulin pour grains' : 'Cafetière pour café moulu'
      );
      this.setText('[data-koota-advisor-cup-price]', '—');
      this.setText('[data-koota-advisor-pack-label]', 'Stock indisponible');
      this.setText('[data-koota-advisor-pack-price]', '—');
      this.setText('[data-koota-advisor-pack-insight]', 'Vous pouvez consulter les cafés du même format ou recommencer le questionnaire.');
      const stockNote = this.querySelector('[data-koota-advisor-stock-note]');
      if (stockNote) {
        stockNote.hidden = false;
        stockNote.textContent = 'Aucun ajout au panier n’est proposé tant qu’un produit compatible n’est pas disponible.';
      }
      if (this.cartForm) this.cartForm.hidden = true;
      const productLink = this.querySelector('[data-koota-advisor-product-link]');
      if (productLink) {
        productLink.href = this.dataset.fallbackUrl || productLink.href;
        productLink.textContent = 'Voir les autres gammes disponibles';
      }
      window.requestAnimationFrame(() => this.result.querySelector('h3')?.focus({ preventScroll: true }));
    }

    prepareNativeCartForm(event) {
      if (!this.activePlan || !this.activePlan.product?.variantId) {
        event.preventDefault();
        if (this.cartStatus) {
          this.cartStatus.textContent = 'La recommandation doit être recalculée avant l’ajout.';
        }
        return;
      }

      const plan = this.activePlan;
      const variantInput = this.querySelector('[data-koota-advisor-variant]');
      const quantityInput = this.querySelector('[data-koota-advisor-quantity]');
      const machineProperty = this.querySelector('[data-koota-advisor-machine-property]');
      const profileProperty = this.querySelector('[data-koota-advisor-profile-property]');

      if (variantInput) variantInput.value = String(plan.product.variantId);
      if (quantityInput) quantityInput.value = String(Math.max(1, Number(plan.quantity || 1)));
      if (machineProperty) machineProperty.value = String(this.answers.machine || '');
      if (profileProperty) profileProperty.value = String(this.answers.taste || '');

      if (this.cartStatus) this.cartStatus.textContent = 'Ajout au panier…';
      this.pushEvent('koota_home_quiz_add_to_cart', {
        product_id: String(plan.product.id),
        variant_id: String(plan.product.variantId),
        quantity: plan.quantity,
        transport: 'shopify-ajax-cart',
      });
    }

    restart() {
      this.answers = {};
      this.activePlan = null;
      this.querySelectorAll('[data-koota-advisor-answer]').forEach((button) => {
        button.classList.remove('is-selected');
        button.setAttribute('aria-pressed', 'false');
      });
      this.result.hidden = true;
      this.result.classList.remove('is-visible');
      if (this.cartForm) this.cartForm.hidden = false;
      if (this.cartStatus) this.cartStatus.textContent = '';
      const addButton = this.querySelector('[data-koota-advisor-add]');
      if (addButton) addButton.disabled = false;
      this.setText('[data-koota-advisor-add-label]', 'Ajouter au panier');
      this.intro.hidden = false;
      this.flow.hidden = true;
      this.querySelector('[data-koota-advisor-start]')?.focus({ preventScroll: true });
    }

    setText(selector, value) {
      const element = this.querySelector(selector);
      if (element) element.textContent = String(value || '');
    }

    pushEvent(name, parameters = {}) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: name,
        quiz_source: 'HOME_KOOTA',
        preparation: this.answers.format,
        taste_profile: this.answers.taste,
        pack_quantity: this.answers.pack,
        ...parameters,
      });
    }
  }

  customElements.define('koota-home-advisor', KootaHomeAdvisor);
}

(() => {
  const cards = Array.from(document.querySelectorAll('[data-koota-home-product-card]'));
  if (!cards.length) return;
  if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    cards.forEach((card) => card.classList.add('is-revealed'));
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-revealed');
      observer.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.16 });

  cards.forEach((card, index) => {
    card.style.setProperty('--koota-card-delay', `${Math.min(index * 90, 270)}ms`);
    observer.observe(card);
  });
})();
